import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { externalLinks } from '../data/mock';
import { ExternalLink, Users, Utensils, Camera, Star, Moon, Sun, Clock } from 'lucide-react';

const Pricing = () => {
  const { language, t } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState('night');

  const packages = {
    day: {
      id: 'day',
      nameKr: '낮 패키지',
      nameEn: 'Day Package',
      timeKr: '11:00 - 18:00 (7시간)',
      timeEn: '11:00 - 18:00 (7 Hours)',
      priceKr: '₩55,000부터',
      priceEn: 'From ₩55,000',
      descKr: '밝은 채광과 함께 즐기는 데이 파티 (기본 4인)',
      descEn: 'Daytime party with natural sunlight (Base: 4 people)',
      icon: <Sun className="text-[#00FFD1]" size={24} />
    },
    night: {
      id: 'night',
      nameKr: '밤 패키지',
      nameEn: 'Night Package',
      timeKr: '19:00 - 10:00 (+1) (15시간)',
      timeEn: '19:00 - 10:00 (+1) (15 Hours)',
      priceKr: '요금 문의',
      priceEn: 'Contact for pricing',
      descKr: '야경과 불멍을 즐기는 올나잇 파티',
      descEn: 'All-night party with city view & firepit',
      icon: <Moon className="text-[#00FFD1]" size={24} />
    },
    full: {
      id: 'full',
      nameKr: '올데이 패키지',
      nameEn: 'Full Day Package',
      timeKr: '11:00 - 10:00 (+1) (23시간)',
      timeEn: '11:00 - 10:00 (+1) (23 Hours)',
      priceKr: '요금 문의',
      priceEn: 'Contact for pricing',
      descKr: '하루 종일 여유롭게 즐기는 통대관',
      descEn: 'Relax all day with exclusive full access',
      icon: <Clock className="text-[#00FFD1]" size={24} />
    }
  };

  const currentPackage = packages[selectedPackage];

  return (
    <section className="bg-black py-16 md:py-24 animate-fade-in">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {language === 'ko' ? '이용 패키지 안내' : 'Booking Packages'}
          </h2>
          <p className="text-white/60">
            {language === 'ko' ? '원하는 시간대를 선택해 주세요' : 'Choose your preferred time slot'}
          </p>
          {/* Rating */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="currentColor" />
              ))}
            </div>
            <span className="text-white/80 text-sm">5.0</span>
            <span className="text-white/50 text-sm">({language === 'ko' ? '21 리뷰' : '21 reviews'})</span>
          </div>
        </div>

        {/* Package Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {Object.values(packages).map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg.id)}
              className={`px-6 py-3 text-sm md:text-base font-medium transition-all flex items-center gap-2 ${
                selectedPackage === pkg.id
                  ? 'bg-[#00FFD1] text-black'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              style={{ borderRadius: 0 }}
            >
              {selectedPackage === pkg.id && pkg.icon}
              {language === 'ko' ? pkg.nameKr : pkg.nameEn}
            </button>
          ))}
        </div>

        {/* Pricing Card */}
        <div className="bg-[#121212] border border-white/10 p-6 md:p-10 text-center">
          
          <div className="flex flex-col items-center justify-center mb-8">
            <div className="p-4 bg-white/5 rounded-full mb-4 text-[#00FFD1]">
              {currentPackage.icon}
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {language === 'ko' ? currentPackage.nameKr : currentPackage.nameEn}
            </h3>
            <p className="text-xl text-[#00FFD1] font-semibold mb-2">
              {language === 'ko' ? currentPackage.timeKr : currentPackage.timeEn}
            </p>
            <p className="text-2xl md:text-3xl font-bold text-white mb-4">
              {language === 'ko' ? currentPackage.priceKr : currentPackage.priceEn}
            </p>
            <p className="text-white/60 max-w-md mx-auto">
              {language === 'ko' ? currentPackage.descKr : currentPackage.descEn}
            </p>
          </div>

          <div className="border-t border-white/10 pt-8 mb-8">
             <p className="text-white/80 mb-4 font-medium">
               {language === 'ko' 
                 ? '추가 인원: 1인당 ₩15,000 (기본 4인 초과 시)' 
                 : 'Extra person: ₩15,000 per person (over 4 people)'}
             </p>
             <p className="text-white/50 text-sm mb-2">
               {language === 'ko' 
                 ? '* 주말 및 성수기 추가 요금이 있을 수 있습니다.' 
                 : '* Weekend and peak season surcharges may apply.'}
             </p>
             <p className="text-white/50 text-sm">
               {language === 'ko' 
                 ? '* BBQ 세트: ₩40,000부터 (인원에 따라 상이)' 
                 : '* BBQ set: From ₩40,000 (varies by group size)'}
             </p>
          </div>

          {/* CTA */}
          <a
            href={externalLinks.naverStore}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full md:w-2/3 mx-auto py-4 bg-[#00FFD1] text-black font-semibold text-lg transition-all duration-300 hover:bg-[#00FFD1]/80 hover:scale-105"
            style={{ borderRadius: 0 }}
          >
            {language === 'ko' ? '실시간 요금 확인 및 예약하기' : 'Check Prices & Book Now'}
            <ExternalLink size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
